/**
 * navigation/pendingInvite.ts
 *
 * "I was in the middle of accepting an invitation when you asked me to sign in."
 *
 * ─── Why this exists ────────────────────────────────────────────────────────
 *
 * Accepting an invitation as an EXISTING user needs a session — the API has to
 * know who is accepting, and the email-match check against the invited address is
 * what stands in for proof of ownership. Someone who opens the link in a signed-out
 * browser therefore has to sign in first.
 *
 * That was handled by telling them so:
 *
 *   "Sign in with your existing account first, then open this invitation link again."
 *
 * Which is a correct instruction and a bad one. It asks the person to sign in, go
 * back to their inbox, find the mail again and re-click — and it says it at the
 * exact moment they were expecting to be done. The invite screen already knows
 * they have an account before the first paint (`preview.user_exists`); nothing was
 * missing except somewhere to keep the token while they signed in.
 *
 * ─── Why not a route param ──────────────────────────────────────────────────
 *
 * Because sign-in is not one hop. login() can end in MFA, resend-verification,
 * resume-onboarding, or the app — five exits, two of which reach the shell. A
 * token threaded as a param has to be re-passed at every one of them, and the day
 * someone adds a sixth exit it is silently dropped. Module state is read by
 * whichever exit actually happens.
 *
 * ─── Read once ──────────────────────────────────────────────────────────────
 *
 * `take` clears as it reads. A pending invite that survived its own redemption
 * would re-fire on the next ordinary sign-in, sending someone who just wanted to
 * check their dashboard back to an invitation they already accepted.
 */

const STORAGE_KEY = 'sk.pendingInviteToken';

let token: string | null = null;

/** Remember the invite we are stepping away from. */
export function setPendingInvite(next: string): void {
  token = next;
  // Module state alone is enough for an in-app navigate — nothing reloads the
  // bundle. sessionStorage is the belt for a hard refresh mid-sign-in, and dies
  // with the tab, which is the right lifetime for this: it is one journey, not a
  // preference.
  //
  // Guarded with `typeof window`, matching authChannel.ts, rather than by importing
  // Platform — this stays a dependency-free module, which is also what makes it
  // testable in a repo whose jest preset cannot transform react-native.
  if (typeof window !== 'undefined') {
    try { window.sessionStorage.setItem(STORAGE_KEY, next); } catch { /* private mode */ }
  }
}

/** Read and clear. Returns null when there is nothing to resume. */
export function takePendingInvite(): string | null {
  let found = token;
  if (!found && typeof window !== 'undefined') {
    try { found = window.sessionStorage.getItem(STORAGE_KEY); } catch { found = null; }
  }
  clearPendingInvite();
  return found || null;
}

export function clearPendingInvite(): void {
  token = null;
  if (typeof window !== 'undefined') {
    try { window.sessionStorage.removeItem(STORAGE_KEY); } catch { /* private mode */ }
  }
}
